import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MainMenu,
  Menu,
  Left,
  Right,
  ProjectsName,
  Viewer,
  Timer,
  Record,
  Action,
  Icon,
  BottomSection,
  Check,
  InputValue,
  Box,
  Top,
  Bottom,
  ProjectTitle,
  ProjectItems,
  Content,
  Label
} from './styles';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { useHistory, useLocation } from 'react-router-dom';
import { ROUTES } from '../../../routes/urls';
import { ProjectItem } from '../ProjectItem';
import { useSelector, useDispatch } from 'react-redux';
import { formatTime, isEmpty, isMediaRecordingSupported } from '../../../helpers/common';
import { setCurrentStartTime } from '../../../actions/timeTrackingActions';
import { CUSTOMER_MEMBER_ROLE } from '../../../global/constants';
import NavbarRecording from './recordingNavbar';
import { RootReducerInterface } from '../../../interfaces/RootReducerInterface';

import {
  DragDropContext,
  DraggableProvided,
  Draggable,
  Droppable,
  DraggableStateSnapshot,
  DropResult
} from 'react-beautiful-dnd';
import { updateWorkspaceProjectPreference } from '../../../helpers/firebaseHelper';
import { useMobileDevice } from '../../../global/useMobile';
import { rootStore } from '../../../mobx/rootStore';
import { ProjectModel } from '../../../mobx/models/project';
import { observer } from 'mobx-react-lite';
import HelpSupportDropdown from './helpSupportDropdown';
import { TaskModel } from '../../../mobx/models/task';
// eslint-disable-next-line @typescript-eslint/no-redeclare
declare type DraggableProvided = typeof import('react-beautiful-dnd');
// eslint-disable-next-line @typescript-eslint/no-redeclare
declare type DraggableStateSnapshot = typeof import('react-beautiful-dnd');

// eslint-disable-next-line @typescript-eslint/no-redeclare
declare type DropResult = typeof import('react-beautiful-dnd');

interface Props {
  isAdmin: any;
  favoriteProjects: ProjectModel[];
  trackingActivity: any;
  onClickStartStop: (itemId: string, time: string, item: any) => void;
  onCloseNavbar?: () => void;
  setOpenContactUsModal?: () => void;
  onClickOpenModel?: () => void;
}

interface MenuItemProps {
  path: string;
  iconName: string;
  label: string;
  count?: number; // Optional count for the right side
  className?: string;
  isActive: boolean;
}

export const MenuItem: React.FC<MenuItemProps> = ({ path, iconName, className, label, count, isActive }) => {
  //Other variable
  const history = useHistory();

  return (
    <Menu
      onClick={() => {
        history.push(path);
      }}
      isActive={isActive}
      className={isActive ? 'active-navbar-item-menu ' : 'navbar-menu-name'}>
      <Left isActive={isActive}>
        <Icon>
          <SVGIcon name={iconName} width='18' height='18' viewBox='0 0 18 18' fill='var(--text-primary)' />
        </Icon>
        <Label>{label}</Label>
      </Left>
      {count && count > 0 ? <Right className='count-number'>{count}</Right> : <></>}
    </Menu>
  );
};

const NavbarContent: React.FC<Props> = (props) => {
  const {
    isAdmin,
    favoriteProjects,
    trackingActivity,
    onClickStartStop,
    onCloseNavbar,
    setOpenContactUsModal,
    onClickOpenModel
  } = props;
  //States
  const [currentActiveTask, setCurrentActiveTask] = useState<TaskModel | null>();

  //use selector state variables
  const stateSelector = useSelector((state: RootReducerInterface) => state);
  const {
    workspace: workspaceSelector,
    timeTracking: timeTrackingSelector,
    invoice: invoiceSelector
  } = stateSelector || {};
  const { workspace: workspaceDetail } = workspaceSelector;
  const { invoices } = invoiceSelector;
  const { apps, customerRole } = workspaceDetail || {};
  const { currentStartTime } = timeTrackingSelector;

  //Mobx store
  const { notificationStore } = rootStore;
  const { notifications } = notificationStore;

  const invoiceCount = invoices?.filter((item: any) => item?.statusText !== 'PAID').length;

  const inboxCount = notifications.filter((item) => !item?.isRead).length;

  //Other variable
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const mobile = useMobileDevice();

  const updateElapsedTime = useCallback(async () => {
    const startDate = new Date(trackingActivity[0]?.TimerStartedAt).getTime();
    const currentDate = new Date().getTime();
    const seconds = Math.ceil((currentDate - startDate) / 1000);
    dispatch(setCurrentStartTime({ task_id: trackingActivity[0]?.TaskId, time: seconds }));
    const activeTask = rootStore.taskStore.tasks.find((task) => task.id === trackingActivity[0]?.TaskId);
    if (activeTask) setCurrentActiveTask(activeTask);
  }, [dispatch, trackingActivity]);

  useEffect(() => {
    let intervalId: any;
    if (trackingActivity?.length > 0) intervalId = setInterval(updateElapsedTime, 1000);
    else {
      if (intervalId) {
        clearInterval(intervalId);
        dispatch(setCurrentStartTime({ task_id: undefined, time: 0 }));
      }
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
        dispatch(setCurrentStartTime({ task_id: undefined, time: 0 }));
      }
    };
  }, [dispatch, trackingActivity, updateElapsedTime]);

  const onClickTaskDetails = useCallback(() => {
    history.push(`/projects/details/${currentActiveTask?.projectId}/tasks/details/${currentActiveTask?.id}`);
  }, [history, currentActiveTask]);

  const calculateUpdatedTaskIndex = useCallback(
    (destinationIndex: number, sourceIndex: number) => {
      let updatedTaskIndex;
      if (destinationIndex === 0) {
        const nextTaskIndex = favoriteProjects?.[0]?.index;
        updatedTaskIndex = nextTaskIndex ? nextTaskIndex - 500 : new Date().getTime();
      } else if (destinationIndex === favoriteProjects?.length - 1) {
        const previousTaskIndex = favoriteProjects?.[favoriteProjects?.length - 1]?.index;
        updatedTaskIndex = previousTaskIndex ? previousTaskIndex + 500 : new Date().getTime();
      } else {
        if (destinationIndex > sourceIndex) {
          const previousTaskIndex: number = favoriteProjects?.[destinationIndex]?.index || 0;
          const nextTaskIndex: number = favoriteProjects?.[destinationIndex + 1]?.index || 0;
          updatedTaskIndex = (previousTaskIndex + nextTaskIndex) / 2;
        } else {
          const previousTaskIndex: number = favoriteProjects?.[destinationIndex - 1]?.index || 0;
          const nextTaskIndex: number = favoriteProjects?.[destinationIndex]?.index || 0;
          updatedTaskIndex = (previousTaskIndex + nextTaskIndex) / 2;
        }
      }

      return updatedTaskIndex;
    },
    [favoriteProjects]
  );

  const handleDragEnd = async (item: DropResult) => {
    try {
      const { destination, draggableId, source } = item || {};
      const { index: sourceIndex } = source || {};
      const { index: destinationIndex } = destination;
      const updatedTaskIndex = calculateUpdatedTaskIndex(destinationIndex, sourceIndex);

      const updatedProject = favoriteProjects.find((project) => project.id === draggableId);
      if (updatedProject) {
        updatedProject.setIndex(updatedTaskIndex);
      }
      // Update project index in workspace preference
      await updateWorkspaceProjectPreference(draggableId, { fav_index: updatedTaskIndex });
    } catch (error) {
      console.log('error', error);
    }
  };

  const renderActiveTimeTrackingBox = useMemo(() => {
    return (
      trackingActivity?.length > 0 && (
        <Timer className='timer'>
          {/* <div className='tooltip'>
      <div className='tooltiptext'> */}
          <Box className='box'>
            <Top onClick={onClickTaskDetails} className='top-section'>
              <Check>
                <InputValue type='checkbox' className='checkbox-round' disabled={true} />
              </Check>
              <p>{currentActiveTask?.name}</p>
            </Top>
            <Bottom>
              <Action>
                <SVGIcon
                  name='time-icon'
                  width='18'
                  height='18'
                  viewBox='0 0 18 18'
                  fill='none'
                  className='time-icon'
                />
                <span>{formatTime(currentStartTime?.time)}</span>{' '}
              </Action>
              <Record
                onClick={() =>
                  onClickStartStop(trackingActivity[0]['_id'], trackingActivity[0]?.TimerStartedAt, trackingActivity[0])
                }>
                <SVGIcon name='record-icon' width='8' height='8' viewBox='0 0 8 8' className='record-icon' />
              </Record>
            </Bottom>
          </Box>
        </Timer>
      )
    );
  }, [currentActiveTask?.name, currentStartTime?.time, onClickStartStop, onClickTaskDetails, trackingActivity]);

  return (
    <>
      <MainMenu>
        <MenuItem
          path='/'
          isActive={location?.pathname === '/'}
          iconName='navbar-inbox-icon'
          label='Inbox'
          count={inboxCount}
        />
        <MenuItem
          path='/my-tasks'
          isActive={location?.pathname?.startsWith('/my-tasks')}
          label='My Tasks'
          iconName='navbar-MyTask-icon'
        />
        {(isEmpty(apps?.messages) || apps?.messages === true) && (
          <MenuItem
            path='/messages'
            isActive={location?.pathname?.startsWith('/messages')}
            iconName='navbar-Messages-icon'
            label='Messages'
          />
        )}
        {/* <MenuItem
          path='/views'
          isActive={location?.pathname?.startsWith('/views')}
          label='Views'
          iconName='navbar-views-icon'
          className='fill-color'
        /> */}
        {(isAdmin || customerRole === CUSTOMER_MEMBER_ROLE.FULL_ACCESS) &&
          (isEmpty(apps?.invoiceAndPayment) || apps?.invoiceAndPayment === true) && (
            <MenuItem
              path='/invoices'
              isActive={location?.pathname?.startsWith('/invoices')}
              iconName='navbar-Invoice-icon'
              label='Invoices'
              count={invoiceCount}
            />
          )}
        {favoriteProjects?.length === 0 && (
          <MenuItem
            path='/projects'
            isActive={location?.pathname?.startsWith('/projects')}
            iconName='navbar-project-icon'
            label='Projects'
          />
        )}
        {isAdmin && (isEmpty(apps?.customer) || apps?.customer === true) && (
          <MenuItem
            path='/customer'
            isActive={location?.pathname?.startsWith('/customer')}
            iconName='navbar-Customer-icon'
            label='Customers'
          />
        )}
        {(isEmpty(apps?.timeTracking) || apps?.timeTracking === true) &&
          customerRole !== CUSTOMER_MEMBER_ROLE.PROJECT_ONLY &&
          customerRole !== CUSTOMER_MEMBER_ROLE.FULL_ACCESS && (
            <MenuItem
              path='/timeTracking'
              isActive={location?.pathname?.startsWith('/timeTracking')}
              iconName='navbar-TimeTracking-icon'
              label='Time Tracking'
            />
          )}
        {isAdmin && (
          <MenuItem
            path={mobile ? ROUTES.REPORT : ROUTES.REPORT_PAYMENTS}
            isActive={location?.pathname?.startsWith('/report')}
            iconName='navbar-Report-icon'
            label='Reports'
          />
        )}
      </MainMenu>
      <Content isTrackingActivity={trackingActivity?.length > 0}>
        <DragDropContext onDragEnd={(result: DropResult) => handleDragEnd(result)}>
          <Droppable droppableId='favrouite_project'>
            {(provided: DraggableProvided) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {favoriteProjects?.length > 0 && (
                  <ProjectsName isTrackingActivity={trackingActivity?.length > 0}>
                    <ProjectTitle className='project'>Projects</ProjectTitle>
                    <ProjectItems>
                      {favoriteProjects?.map((item, index: number) => (
                        <Draggable key={item?.id} draggableId={item?.id} index={index}>
                          {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                              <ProjectItem
                                key={item?.id}
                                item={item}
                                onCloseNavbar={onCloseNavbar ? onCloseNavbar : undefined}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}

                      {provided?.placeholder}
                      <Viewer onClick={() => history.push('/projects')}>
                        <Icon className='all-project'>
                          <SVGIcon
                            name='three-dots-horizontal-icon'
                            width='18'
                            height='18'
                            viewBox='0 0 18 18'
                            fill='var(--text-primary)'
                          />
                        </Icon>
                        All Projects
                      </Viewer>
                    </ProjectItems>
                  </ProjectsName>
                )}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <BottomSection className='navbar-bottom'>
          {isMediaRecordingSupported() && <NavbarRecording />}

          {/* {(workspaceDetail?.isOwner || workspaceDetail?.isAdmin)
      <InviteSection onClick={onClickOnInviteMember}>
        {themeMode?.theme === 'dark' ? (
          <SVGIcon name='light-invite-member-icon' width='18' height='18' viewBox='0 0 18 18' fill='none' />
        ) : (
          <SVGIcon name='dark-invite-member-icon' width='18' height='18' viewBox='0 0 18 18' fill='none' />
        )}
        <p>Invite Member</p>
      </InviteSection>
    } */}
          {/* tracker will only show in desktop */}
          {!mobile && renderActiveTimeTrackingBox}

          <HelpSupportDropdown setOpenContactUsModal={setOpenContactUsModal} onClickOpenModel={onClickOpenModel} />
        </BottomSection>
      </Content>
    </>
  );
};

export default observer(NavbarContent);
